
import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useUserTracking } from '@/hooks/useUserTracking';
import { useDataLayer } from '@/hooks/useDataLayer';
import { X } from 'lucide-react';

interface WhatsAppPopupProps {
  isWhatsAppOpen: boolean;
  setIsWhatsAppOpen: (open: boolean) => void;
  whatsAppFormData: any;
  setWhatsAppFormData: (data: any) => void;
  handleWhatsAppFormSubmit: (e: React.FormEvent) => void;
  handleWhatsAppPhoneChange: (value: string) => void;
  handleCountryChange: (countryName: string, formType: 'main' | 'whatsapp') => void;
  handleWhatsAppFormFieldChange: (field: string, value: string) => void;
  handleWhatsAppOpen: (procedure?: string) => void;
  hasStartedWhatsAppForm: boolean;
}

const WhatsAppIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059s-.018-.458.13-.606c.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.488"/>
  </svg>
);

// Lazy-load data
let countriesCache: any[] | null = null;
let procedureGroupsCache: any[] | null = null;

const useFormData = () => {
  const [countries, setCountries] = useState<any[]>(countriesCache || []);
  const [procedureGroups, setProcedureGroups] = useState<any[]>(procedureGroupsCache || []);

  useEffect(() => {
    if (!countriesCache) {
      import('@/data/countries').then(m => {
        countriesCache = m.countries;
        setCountries(m.countries);
      });
    }
    if (!procedureGroupsCache) {
      import('@/data/procedures').then(m => {
        procedureGroupsCache = m.procedureGroups;
        setProcedureGroups(m.procedureGroups);
      });
    }
  }, []);

  return { countries, procedureGroups };
};

const WhatsAppPopup: React.FC<WhatsAppPopupProps> = ({
  isWhatsAppOpen,
  setIsWhatsAppOpen,
  whatsAppFormData,
  setWhatsAppFormData: _setWhatsAppFormData,
  handleWhatsAppFormSubmit,
  handleWhatsAppPhoneChange,
  handleCountryChange,
  handleWhatsAppFormFieldChange,
  handleWhatsAppOpen,
  hasStartedWhatsAppForm: _hasStartedWhatsAppForm
}) => {
  const trackingData = useUserTracking();
  const _dataLayer = useDataLayer();
  const panelRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { countries, procedureGroups } = useFormData();

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!isWhatsAppOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const radixPortal = (target as Element).closest?.('[data-radix-popper-content-wrapper], [role="listbox"]');
      if (radixPortal) return;
      if (
        panelRef.current && !panelRef.current.contains(target) &&
        btnRef.current && !btnRef.current.contains(target)
      ) {
        setIsWhatsAppOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isWhatsAppOpen, setIsWhatsAppOpen]);

  return (
    <div className="contents">
      {/* Scroll to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Voltar ao topo"
        className={`fixed bottom-28 z-[52] bg-dark-surface border border-white/10 text-light-muted rounded-full w-11 h-11 flex items-center justify-center shadow-lg hover:border-gold/40 hover:text-gold transition-all duration-300 ${showScrollTop ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}`}
        style={{ right: 'calc(2rem + (3.5rem - 2.75rem) / 2)' }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 15l-6-6-6 6"/>
        </svg>
      </button>

      {/* WhatsApp FAB button */}
      <button
        ref={btnRef}
        onClick={() => {
          if (!isWhatsAppOpen) handleWhatsAppOpen();
          setIsWhatsAppOpen(!isWhatsAppOpen);
        }}
        className="fixed bottom-8 right-8 z-[52] bg-light-primary text-dark-base rounded-full w-14 h-14 flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
        style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label="Abrir formulário de agendamento via WhatsApp"
      >
        <WhatsAppIcon />
      </button>

      {/* Compact floating panel */}
      <div
        ref={panelRef}
        className={`
          fixed bottom-6 right-6 z-[53] w-[340px] sm:w-[380px] rounded-2xl shadow-2xl overflow-hidden
          transition-all duration-300 ease-out
          ${isWhatsAppOpen
            ? 'opacity-100 translate-x-0 pointer-events-auto'
            : 'opacity-0 translate-x-full pointer-events-none'
          }
        `}
        style={{ backgroundColor: 'hsl(200, 30%, 90%)' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 pb-2">
          <div>
            <h3 className="text-dark-base text-base font-semibold">
              Fale Conosco pelo WhatsApp
            </h3>
            <p className="text-dark-elevated text-xs mt-0.5">
              Preencha o formulário e nossa equipe entrará em contato via WhatsApp.
            </p>
          </div>
          <button
            onClick={() => setIsWhatsAppOpen(false)}
            className="text-dark-elevated hover:text-dark-base transition-colors p-1 -mt-1 -mr-1"
            aria-label="Fechar formulário"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Warning banner */}
        <div className="mx-4 mb-3 border border-gray-300 rounded-md px-3 py-1.5 text-center" style={{ backgroundColor: '#f5f5f5' }}>
          <span className="text-xs font-semibold text-gray-800">
            ⚠️ IMPORTANTE: Não atendemos convênios
          </span>
        </div>

        {/* Form */}
        <div className="px-4 pb-4 max-h-[55vh] overflow-y-auto">
          <form onSubmit={handleWhatsAppFormSubmit} className="space-y-3">
            <input type="hidden" name="origem" value={trackingData.origem} />
            <input type="hidden" name="midia" value={trackingData.midia} />
            <input type="hidden" name="url" value={trackingData.url} />
            <input type="hidden" name="countryCode" value={whatsAppFormData.countryCode} />

            <div>
              <label htmlFor="wa-name" className="block text-xs font-bold text-gray-800 mb-1">
                Nome completo:
              </label>
              <Input
                id="wa-name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={whatsAppFormData.name}
                onChange={(e) => handleWhatsAppFormFieldChange('name', e.target.value)}
                className="border-gray-300 bg-white text-gray-900 focus:border-gray-500 h-9 text-sm"
                placeholder="Insira seu nome"
              />
            </div>

            <div>
              <label htmlFor="wa-email" className="block text-xs font-bold text-gray-800 mb-1">
                Email:
              </label>
              <Input
                id="wa-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={whatsAppFormData.email}
                onChange={(e) => handleWhatsAppFormFieldChange('email', e.target.value)}
                className="border-gray-300 bg-white text-gray-900 focus:border-gray-500 h-9 text-sm"
                placeholder="Insira seu email"
              />
            </div>

            <div>
              <label htmlFor="wa-country" className="block text-xs font-bold text-gray-800 mb-1">
                País:
              </label>
              <Select name="country" value={whatsAppFormData.country} onValueChange={(value) => handleCountryChange(value, 'whatsapp')}>
                  <SelectTrigger id="wa-country" className="border-gray-300 bg-white text-gray-900 focus:border-gray-500 h-9 text-sm">
                    <SelectValue placeholder="Selecione seu país" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-40 z-[60]">
                    {countries.map((country) => (
                      <SelectItem key={country.name} value={country.name} className="text-gray-900 hover:bg-gray-100">
                        {country.name} ({country.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
            </div>

            <div>
              <label htmlFor="wa-phone" className="block text-xs font-bold text-gray-800 mb-1">
                Telefone/WhatsApp:
              </label>
              <Input
                id="wa-phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                required
                value={whatsAppFormData.phone}
                onChange={(e) => handleWhatsAppPhoneChange(e.target.value)}
                className="border-gray-300 bg-white text-gray-900 focus:border-gray-500 h-9 text-sm"
                placeholder={whatsAppFormData.country === 'Brasil' ? '(XX) XXXXX-XXXX' : 'Número de telefone'}
                maxLength={whatsAppFormData.country === 'Brasil' ? 15 : 20}
              />
            </div>

            <div>
              <label htmlFor="wa-procedure" className="block text-xs font-bold text-gray-800 mb-1">
                Procedimento de interesse:
              </label>
              <Select
                  name="procedure"
                  value={whatsAppFormData.procedure || ''}
                  onValueChange={(value) => handleWhatsAppFormFieldChange('procedure', value)}
                >
                  <SelectTrigger id="wa-procedure" className="border-gray-300 bg-white text-gray-900 focus:border-gray-500 h-9 text-sm">
                    <SelectValue placeholder="Selecione um procedimento" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 max-h-52 z-[60]">
                    {procedureGroups.map((group) => (
                      <div key={group.label}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          {group.label}
                        </div>
                        {group.options.map((option: string) => (
                          <SelectItem
                            key={option}
                            value={option}
                            className="text-gray-900 hover:bg-gray-100 cursor-pointer pl-4"
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
            </div>

            {whatsAppFormData.procedure === 'Outro' && (
              <div>
                <label htmlFor="wa-custom" className="block text-xs font-bold text-gray-800 mb-1">
                  Especifique o motivo *
                </label>
                <Input
                  id="wa-custom"
                  name="customProcedure"
                  type="text"
                  required
                  value={whatsAppFormData.customProcedure}
                  onChange={(e) => handleWhatsAppFormFieldChange('customProcedure', e.target.value)}
                  className="border-gray-300 bg-white text-gray-900 focus:border-gray-500 h-9 text-sm"
                  placeholder="Descreva o procedimento"
                />
              </div>
            )}

            <div className="pt-1">
              <Button
                type="submit"
                className="w-full bg-dark-base hover:bg-dark-elevated text-light-primary font-semibold h-10 text-sm rounded-lg"
              >
                Enviar e Agendar
              </Button>
            </div>

            <p className="text-xs font-bold text-red-600 text-center">
              ⚠️ ATENÇÃO: Não atendemos convênios
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppPopup;
